import {DtoDetail} from "features/core/shared/dto/dto.detail";
import {ProvisionAgentRoleEnum} from "../../../enum/provision-agent-role.enum";
import {ProvisionContractAgentSource} from "../../../enum/provision-contract-agent-source.enum";
import {ProvisionLiabilitySource} from "../../../enum/provision-liability-source.enum";
import {ProvisionMatrixCalcMode} from "../../../enum/provision-matrix-calc-mode.enum";
import {ProvisionLiabilityStatus} from "../../../enum/provision-liability-status.enum";
import {ProvisionBaseCalculationMode} from "../../../enum/provision-base-calculation-mode.enum";
import type {ProvisionContractDto} from "./provision-contract.dto";
import type {ProvisionTypeProvisionBaseDto} from "./provision-type-provision-base.dto";
import type {ProvisionTypeComponentDto} from "./provision-type-component.dto";
import type {ProvisionAgentDto} from "./provision-agent.dto";
import type {ProvisionTypeDto} from "./provision-type.dto";
import type {ProvisionTypeContractEventDto} from "./provision-type-contract-event.dto";
import type {ProvisionTypeMatrixDto} from "./provision-type-matrix.dto";
import type {ProvisionLiabilityTypeDto} from "./provision-liability-type.dto";
import type {ProvisionTypeCalculatedAgentDto} from "./provision-type-calculated-agent.dto";

/**
 * R-08 ProvisionContractAgent — the result row of one (event, provision component, agent) triple
 * (04-frontend.md §2.6). Renamed from the former liability entity in WP-E5 and turned
 * from a 1:1 extension of R-07 into an n:1 child of it (A6-04): a single ProvisionContract event
 * row now fans out into one row here per ProvisionTypeComponent and per ProvisionAgent. contract,
 * provisionType and provisionTypeContractEvent are denormalised (ANF + direct JDBC selection
 * path) in addition to the provisionContract FK. Own lifecycle: created with the first booking,
 * carried forward per run, frozen on Storno, NEVER deleted. Audited (traceability, TR-002).
 */
export class ProvisionContractAgentDto extends DtoDetail {

    public provisionContractDto?: ProvisionContractDto;
    public provisionContractDtoId: number;
    public provisionContractDtoName: string;

    public provisionTypeComponentDto?: ProvisionTypeComponentDto;
    public provisionTypeComponentDtoId: number;
    public provisionTypeComponentDtoName: string;

    public provisionAgentDto?: ProvisionAgentDto;
    public provisionAgentDtoId: number;
    public provisionAgentDtoName: string;

    /**
     * The role this row was paid for (A2-06). The cp-local role enum, NOT the cm one: on top of
     * the three cm roles it carries `calculated`, the row a ProvisionTypeCalculatedAgent rule (K-17)
     * derived instead of a cm.ContractAgent link. Ordinals 0-2 stay aligned with cm, so the type
     * change was migration-free.
     */
    public agentRole: ProvisionAgentRoleEnum;
    /** AUTOMATIC = produced by the engine (replaceable on recalculation); MANUAL = never overwritten. */
    public source: ProvisionContractAgentSource;
    public agentManuallyChanged: boolean;

    /**
     * The calculated-agent rule (K-17) that produced this row; NULL for every row a cm.ContractAgent
     * link produced. Three orthogonal statements, no redundancy: `source` says WHO created the row
     * (engine vs. human) and stays AUTOMATIC, `agentRole` says WHAT it is (`calculated`), and this
     * FK says WHY — which is why ProvisionContractAgentSource deliberately gained no CALCULATED
     * value (D-6/AB-6).
     */
    public provisionTypeCalculatedAgentDto?: ProvisionTypeCalculatedAgentDto;
    public provisionTypeCalculatedAgentDtoId?: number;
    public provisionTypeCalculatedAgentDtoName?: string;

    /** Denormalised (ANF). */
    public contractDtoId: number;
    public contractDtoName: string;
    /** Denormalised (ANF). */
    public provisionTypeDto?: ProvisionTypeDto;
    public provisionTypeDtoId: number;
    public provisionTypeDtoName: string;
    /** Denormalised (ANF). */
    public provisionTypeContractEventDto?: ProvisionTypeContractEventDto;
    public provisionTypeContractEventDtoId: number;
    public provisionTypeContractEventDtoName: string;

    public provisionTypeMatrixDto?: ProvisionTypeMatrixDto;
    public provisionTypeMatrixDtoId?: number;
    public provisionTypeMatrixDtoName?: string;

    /** Liability type from the K-07 assignment, before an absolute matrix overwrite (A3-05). */
    public liabilityTypeAssignmentDto?: ProvisionLiabilityTypeDto;
    public liabilityTypeAssignmentDtoId?: number;
    public liabilityTypeAssignmentDtoName?: string;
    /** Effective (applied) liability type — assignment result, or the matrix overwrite when set. */
    public liabilityTypeDto?: ProvisionLiabilityTypeDto;
    public liabilityTypeDtoId?: number;
    public liabilityTypeDtoName?: string;
    /** Where the applied liability type came from — ASSIGNMENT or MATRIX_OVERWRITE (A3-05). */
    public appliedLiabilitySource?: ProvisionLiabilitySource;

    public appliedCalcMode?: ProvisionMatrixCalcMode;
    public appliedFactor?: number;
    public appliedReservePercentage?: number;

    /**
     * The six base-figure columns (01-requirement.md §7) that keep it traceable which K-04 base a
     * claim was calculated from — resolved PER COMPONENT (K-06/K-18) since WP for the renewal
     * commission. The type-wide event row `cp_provision_contract` keeps its own six base columns
     * unchanged, still filled from the type-wide K-06 row (provisionTypeComponent = NULL) — no
     * backfill, the pre-existing rows stay valid as-is.
     */
    public provisionBaseDto?: ProvisionTypeProvisionBaseDto;
    public provisionBaseDtoId?: number;
    public provisionBaseDtoName?: string;
    /** DIFFERENCE or FULL — the base-calculation mode actually applied to this claim. */
    public appliedBaseCalculationMode?: ProvisionBaseCalculationMode;
    /** Monthly reference (subtrahend) amount used for a DIFFERENCE base. */
    public baseReferenceAmountMonthly?: number;
    /** Resulting monthly base amount after DIFFERENCE/FULL was applied. */
    public baseAmountMonthly?: number;
    /** Base amount accumulated over appliedBaseTermMonths (capped at maxTermMonths). */
    public baseAmountTotal?: number;
    /** Term in months over which baseAmountTotal was accumulated. */
    public appliedBaseTermMonths?: number;

    public provisionAmount?: number;
    /** Manual overwrite of provisionAmount; NULL = no overwrite (AX-09). */
    public provisionAmountOverwrite?: number;
    /** Provenance of provisionAmountOverwrite — set only when the overwrite is set. */
    public overwriteSource?: ProvisionContractAgentSource;
    /** Mandatory reason text of the overwrite (Pflichtgrund). */
    public overwriteReason?: string;

    /** Target reserve share of the last run (after cap accumulator, U-05 c). */
    public reserveTargetAmount?: number;
    /** provisionAmount - reserveTargetAmount. */
    public provisionTargetAmount?: number;

    public totalAmount: number;
    public liabilityMonths: number;
    public liabilityStartDate: Date;
    /** INCLUSIVE end (D-29); extends by payment-free months (U-02). */
    public liabilityEndDate: Date;
    /** Cumulative payment-free months counted up to the cutoff — audit/extension size (V-001). */
    public paymentFreeMonths: number;
    public inLiabilityAmount: number;
    public outOfLiabilityAmount: number;
    public liabilityStatus: ProvisionLiabilityStatus;

    /** Effective (frozen) Storno date. */
    public cancellationDate?: Date;
    /** inLiabilityAmount at the Storno date. */
    public cancellationLiabilityAmount?: number;

}
